import { Platform } from 'react-native';
import SocketManager from '@utils/socket';

export type ContextEntryType =
	| 'user_interaction'
	| 'notification'
	| 'ai_request'
	| 'tensorflow_request'
	| 'document_viewing';

export interface ContextEntry {
	id: string;
	timestamp: string;
	type: ContextEntryType;
	content: string;
	tokenCount: number;
	byteCount: number;
	metadata?: Record<string, any>;
}

export interface ContextBreakdown {
	user_interaction: { tokens: number; bytes: number; count: number };
	notification: { tokens: number; bytes: number; count: number };
	ai_request: { tokens: number; bytes: number; count: number };
	tensorflow_request: { tokens: number; bytes: number; count: number };
	document_viewing: { tokens: number; bytes: number; count: number };
}

export interface ContextSettings {
	trackUserInteractions: boolean;
	trackNotifications: boolean;
	trackAIRequests: boolean;
	trackTensorFlowRequests: boolean;
	trackDocumentViewing: boolean;
	userInteractionFilter: 'all' | 'filtered' | 'none';
}

// Context limits - summary should trigger when approaching these limits
const CONTEXT_LIMIT_TOKENS = 200000;
const CONTEXT_LIMIT_BYTES = 200000;

// Summary trigger threshold - only trigger when we reach 200k tokens
// This ensures we wait until we actually reach the limit before summarizing
const SUMMARY_TRIGGER_THRESHOLD_TOKENS = 200000; // Request at 200k as specified
const SUMMARY_TRIGGER_THRESHOLD_BYTES = 200000; // Request at 200k as specified

// Minimum time between summary requests (5 minutes) to prevent too frequent requests
const MIN_SUMMARY_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Approximate token counting: ~4 characters per token
function estimateTokens(text: string): number {
	return Math.ceil(text.length / 4);
}

class ContextTracker {
	private dbInstance: IDBDatabase | null = null;
	private contextQueue: ContextEntry[] = [];
	private isProcessing = false;
	private batchTimeout: NodeJS.Timeout | null = null;
	private readonly batchSize = 10;
	private readonly flushInterval = 2000; // 2 seconds (increased from 1 second)
	private sizeCache: { tokens: number; bytes: number } | null = null;
	private sizeCacheTimestamp: number = 0;
	private readonly sizeCacheTTL = 5000; // Cache size for 5 seconds
	private settings: ContextSettings = {
		trackUserInteractions: true,
		trackNotifications: true,
		trackAIRequests: true,
		trackTensorFlowRequests: true,
		trackDocumentViewing: true,
		userInteractionFilter: 'filtered',
	};
	private lastSummaryTimestamp: number = 0; // Track when last summary was requested
	private summaryCheckTimeout: NodeJS.Timeout | null = null; // Debounce summary checks
	private socketManager: SocketManager | null = null; // Socket manager for notifications
	private socketListenerSetup: boolean = false; // Track if socket listener is set up
	private pendingSummaryJobId: string | null = null; // Track pending summary job ID

	/**
	 * Cleanup method to clear timeouts and prevent memory leaks
	 * Should be called when context tracker is no longer needed
	 */
	cleanup(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
			this.batchTimeout = null;
		}
		if (this.summaryCheckTimeout) {
			clearTimeout(this.summaryCheckTimeout);
			this.summaryCheckTimeout = null;
		}
		// Clean up socket listener
		if (this.socketManager && this.socketListenerSetup) {
			try {
				// Try to get socket - getSocket can work without userId as it uses getId() internally
				const socket = (this.socketManager as any).getSocket?.();
				if (socket) {
					socket.off('notification');
				}
			} catch (error) {
				console.warn('[ContextTracker] Error cleaning up socket listener:', error);
			}
			this.socketListenerSetup = false;
		}
	}

	async initDB(): Promise<IDBDatabase> {
		if (this.dbInstance && this.dbInstance.version > 0)
			return this.dbInstance;

		if (Platform.OS !== 'web') {
			throw new Error('IndexedDB only available on web platform');
		}

		return new Promise((resolve, reject) => {
			const request = indexedDB.open('ContextWindowDB', 1);

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;

				if (!db.objectStoreNames.contains('context')) {
					const store = db.createObjectStore('context', {
						keyPath: 'id',
						autoIncrement: false,
					});
					store.createIndex('timestamp', 'timestamp', {
						unique: false,
					});
					store.createIndex('type', 'type', { unique: false });
				}

				if (!db.objectStoreNames.contains('settings')) {
					db.createObjectStore('settings', { keyPath: 'key' });
				}
			};

			request.onsuccess = (event) => {
				this.dbInstance = (event.target as IDBOpenDBRequest).result;
				this.loadSettings();
				resolve(this.dbInstance);
			};

			request.onerror = (event) => {
				reject((event.target as IDBOpenDBRequest).error);
			};
		});
	}

	private async loadSettings(): Promise<void> {
		if (!this.dbInstance) return;

		try {
			const transaction = this.dbInstance.transaction(
				'settings',
				'readonly'
			);
			const store = transaction.objectStore('settings');
			const request = store.get('contextSettings');

			request.onsuccess = () => {
				if (request.result) {
					this.settings = {
						...this.settings,
						...request.result.value,
					};
				}
			};
		} catch (error) {
			console.error('Failed to load context settings:', error);
		}
	}

	async updateSettings(settings: Partial<ContextSettings>): Promise<void> {
		this.settings = { ...this.settings, ...settings };

		if (!this.dbInstance) {
			await this.initDB();
		}

		try {
			const transaction = this.dbInstance!.transaction(
				'settings',
				'readwrite'
			);
			const store = transaction.objectStore('settings');
			await store.put({ key: 'contextSettings', value: this.settings });
		} catch (error) {
			console.error('Failed to save context settings:', error);
		}
	}

	getSettings(): ContextSettings {
		return { ...this.settings };
	}

	async addContextEntry(
		type: ContextEntryType,
		content: string,
		metadata?: Record<string, any>
	): Promise<void> {
		if (Platform.OS !== 'web') return;

		// Check if this type should be tracked
		if (!this.shouldTrackType(type)) {
			return;
		}

		const tokenCount = estimateTokens(content);
		const byteCount = new Blob([content]).size;

		const entry: ContextEntry = {
			id: `${Date.now()}-${Math.random()}`,
			timestamp: new Date().toISOString(),
			type,
			content,
			tokenCount,
			byteCount,
			metadata,
		};

		this.contextQueue.push(entry);
		this.invalidateSizeCache(); // Invalidate cache when adding new entry
		this.scheduleBatchProcess();

		// Check if we need to trigger summary (use cache=false to get accurate size)
		// Only check after batch is processed to avoid false positives
		// Summary check will happen after batch processing completes
	}

	private shouldTrackType(type: ContextEntryType): boolean {
		switch (type) {
			case 'user_interaction':
				return (
					this.settings.trackUserInteractions &&
					this.settings.userInteractionFilter !== 'none'
				);
			case 'notification':
				return this.settings.trackNotifications;
			case 'ai_request':
				return this.settings.trackAIRequests;
			case 'tensorflow_request':
				return this.settings.trackTensorFlowRequests;
			case 'document_viewing':
				return this.settings.trackDocumentViewing;
			default:
				return false;
		}
	}

	private scheduleBatchProcess(): void {
		if (this.batchTimeout) {
			clearTimeout(this.batchTimeout);
		}

		this.batchTimeout = setTimeout(() => {
			this.processBatch();
		}, this.flushInterval);

		if (this.contextQueue.length >= this.batchSize) {
			this.processBatch();
		}
	}

	private async processBatch(): Promise<void> {
		if (this.isProcessing || this.contextQueue.length === 0) return;

		this.isProcessing = true;

		try {
			const db = await this.initDB();
			const transaction = db.transaction('context', 'readwrite');
			const store = transaction.objectStore('context');

			const batch = this.contextQueue.splice(0, this.batchSize);

			const promises = batch.map(
				(entry) =>
					new Promise<void>((resolve, reject) => {
						const request = store.add(entry);
						request.onsuccess = () => resolve();
						request.onerror = () => {
							// Handle duplicate key errors gracefully
							if (request.error?.name === 'ConstraintError') {
								console.warn(
									'Context entry already exists, skipping:',
									entry.id
								);
								resolve(); // Resolve instead of reject to continue processing
							} else {
								reject(request.error);
							}
						};
					})
			);

			await Promise.all(promises);

			// Invalidate cache after batch is processed
			this.invalidateSizeCache();

			// REMOVED: Automatic summary check - summary should only be triggered:
			// 1. Manually by user via UI button
			// 2. As a one-time background job on the backend when context is full
			// This prevents continuous bombardment of context summary API calls
			// this.scheduleSummaryCheck();

			// If there are more items in queue, schedule next batch
			if (this.contextQueue.length > 0) {
				this.scheduleBatchProcess();
			}
		} catch (error) {
			console.error('Failed to process context batch:', error);
			// Don't re-add items to queue on error to prevent infinite loops
			// Items are already removed from queue, so they'll be lost but won't cause reloads
		} finally {
			this.isProcessing = false;
		}
	}

	async getContextSize(
		useCache: boolean = true
	): Promise<{ tokens: number; bytes: number }> {
		if (Platform.OS !== 'web') return { tokens: 0, bytes: 0 };

		// Use cache if available and not expired
		if (
			useCache &&
			this.sizeCache &&
			Date.now() - this.sizeCacheTimestamp < this.sizeCacheTTL
		) {
			return this.sizeCache;
		}

		try {
			const entries = await this.getContextEntries();
			const tokens = entries.reduce(
				(sum, entry) => sum + entry.tokenCount,
				0
			);
			const bytes = entries.reduce(
				(sum, entry) => sum + entry.byteCount,
				0
			);

			// Update cache
			this.sizeCache = { tokens, bytes };
			this.sizeCacheTimestamp = Date.now();

			return { tokens, bytes };
		} catch (error) {
			console.error('Failed to get context size:', error);
			return { tokens: 0, bytes: 0 };
		}
	}

	// Invalidate size cache (call after adding entries)
	private invalidateSizeCache(): void {
		this.sizeCache = null;
		this.sizeCacheTimestamp = 0;
	}

	async getContextEntries(limit?: number): Promise<ContextEntry[]> {
		if (Platform.OS !== 'web') return [];

		try {
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction('context', 'readonly');
				const store = transaction.objectStore('context');
				const index = store.index('timestamp');
				const request = limit
					? index.getAll(null, limit)
					: index.getAll();

				request.onsuccess = () => {
					const results = request.result.reverse(); // Most recent first
					resolve(results);
				};
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to fetch context entries:', error);
			return [];
		}
	}

	async getContextBreakdown(): Promise<ContextBreakdown> {
		if (Platform.OS !== 'web') {
			return {
				user_interaction: { tokens: 0, bytes: 0, count: 0 },
				notification: { tokens: 0, bytes: 0, count: 0 },
				ai_request: { tokens: 0, bytes: 0, count: 0 },
				tensorflow_request: { tokens: 0, bytes: 0, count: 0 },
				document_viewing: { tokens: 0, bytes: 0, count: 0 },
			};
		}

		try {
			const entries = await this.getContextEntries();
			const breakdown: ContextBreakdown = {
				user_interaction: { tokens: 0, bytes: 0, count: 0 },
				notification: { tokens: 0, bytes: 0, count: 0 },
				ai_request: { tokens: 0, bytes: 0, count: 0 },
				tensorflow_request: { tokens: 0, bytes: 0, count: 0 },
				document_viewing: { tokens: 0, bytes: 0, count: 0 },
			};

			entries.forEach((entry) => {
				if (breakdown[entry.type]) {
					breakdown[entry.type].tokens += entry.tokenCount;
					breakdown[entry.type].bytes += entry.byteCount;
					breakdown[entry.type].count += 1;
				}
			});

			return breakdown;
		} catch (error) {
			console.error('Failed to get context breakdown:', error);
			return {
				user_interaction: { tokens: 0, bytes: 0, count: 0 },
				notification: { tokens: 0, bytes: 0, count: 0 },
				ai_request: { tokens: 0, bytes: 0, count: 0 },
				tensorflow_request: { tokens: 0, bytes: 0, count: 0 },
				document_viewing: { tokens: 0, bytes: 0, count: 0 },
			};
		}
	}

	async clearContext(): Promise<void> {
		if (Platform.OS !== 'web') return;

		try {
			// Wait for any ongoing batch processing to complete
			// This prevents race conditions where entries are added after clearing
			while (this.isProcessing) {
				await new Promise(resolve => setTimeout(resolve, 50));
			}
			
			// Clear pending queue first
			this.contextQueue = [];
			
			// Clear any pending batch processing
			if (this.batchTimeout) {
				clearTimeout(this.batchTimeout);
				this.batchTimeout = null;
			}
			
			// Cancel any pending summary checks
			if (this.summaryCheckTimeout) {
				clearTimeout(this.summaryCheckTimeout);
				this.summaryCheckTimeout = null;
			}
			
			// Invalidate cache
			this.invalidateSizeCache();
			
			// Clear IndexedDB
			const db = await this.initDB();
			return new Promise((resolve, reject) => {
				const transaction = db.transaction('context', 'readwrite');
				const store = transaction.objectStore('context');
				const request = store.clear();

				request.onsuccess = () => {
					// Ensure cache is cleared after DB clear
					this.invalidateSizeCache();
					resolve();
				};
				request.onerror = () => reject(request.error);
			});
		} catch (error) {
			console.error('Failed to clear context:', error);
		}
	}

	private isSummarizing = false; // Prevent concurrent summary generation

	/**
	 * Set up socket listener for context summary notifications
	 * This listens for async summary completion from the backend
	 */
	public async setupSocketListener(): Promise<void> {
		if (this.socketListenerSetup || Platform.OS !== 'web') {
			return;
		}

		try {
			// Get userId for socket authentication
			let userId: string | null = null;
			try {
				const storeModule = await import('@reduxLocal/persistState');
				const store = storeModule.store;
				if (store) {
					const state = store.getState();
					userId = state?.promiseData?.data?.userData?.id;
				}
			} catch (error) {
				console.warn('[ContextTracker] Could not get userId for socket:', error);
			}

			if (!userId) {
				console.warn('[ContextTracker] No userId available, cannot set up socket listener');
				return;
			}

			// Initialize socket manager for notifications namespace
			if (!this.socketManager) {
				this.socketManager = new SocketManager('notifications', {
					autoconnect: false
				});
			}

			const socket = this.socketManager.getSocket(userId);
			if (!socket) {
				console.warn('[ContextTracker] Socket not available for notifications');
				return;
			}

			// Connect socket if not already connected
			if (!socket.connected) {
				socket.connect();
			}

			// Listen for notification events
			socket.on('notification', async (notification: any) => {
				// Only handle context summary notifications
				if (
					notification.type === 'context_summary_complete' ||
					notification.type === 'context_summary_failed'
				) {
					console.log('[ContextTracker] Received context summary notification event:', notification.type);
					await this.handleSummaryNotification(notification);
				}
			});

			this.socketListenerSetup = true;
			console.log('[ContextTracker] Socket listener set up for context summary notifications');
		} catch (error) {
			console.error('[ContextTracker] Failed to set up socket listener:', error);
		}
	}

	/**
	 * Handle summary notification from backend
	 */
	private async handleSummaryNotification(notification: any): Promise<void> {
		if (notification.type === 'context_summary_complete') {
			console.log('[ContextTracker] Received context summary completion notification', notification);

			// Validate notification has summary data
			// Skip notifications from JobNotificationService that don't have summary data
			// Only process notifications from sendSummaryNotification which include the actual summary
			if (!notification.summary || !notification.summary.content) {
				console.warn('[ContextTracker] Summary notification missing content - ignoring notification from JobNotificationService (will wait for sendSummaryNotification)');
				// Don't set isSummarizing to false here - wait for the notification with actual summary data
				return;
			}

			try {
				// Validate summary metadata
				const metadata = notification.summary.metadata || {};
				if (
					metadata.originalEntryCount === undefined ||
					metadata.originalTokenCount === undefined
				) {
					console.warn(
						'[ContextTracker] Summary notification missing some metadata fields, proceeding anyway'
					);
				}

				// When context window is full: clear old context and save summary for reference
				// Step 1: Clear all old context entries to free up space
				await this.clearContext();

				// Step 2: Add the summary as a new context entry for future reference
				// The summary will be included in next AI calls, preserving important information
				await this.addContextEntry('ai_request', notification.summary.content, {
					...metadata,
					isSummary: true,
					originalEntryCount: metadata.originalEntryCount || 0,
					summarizedAt: new Date().toISOString(),
				});

				// Step 3: Wait for batch processing to complete and ensure the summary entry is saved
				// This prevents the summary from being lost and ensures context is properly reset
				while (this.isProcessing || this.contextQueue.length > 0) {
					await new Promise(resolve => setTimeout(resolve, 50));
				}
				
				// Wait an additional tick to ensure IndexedDB transaction completes
				await new Promise(resolve => setImmediate(resolve));

				// Invalidate cache to force immediate refresh
				this.invalidateSizeCache();

				// Dispatch custom event to notify UI components (like ContextProgressRing) to refresh
				if (Platform.OS === 'web' && typeof window !== 'undefined') {
					window.dispatchEvent(
						new CustomEvent('contextSummaryGenerated', {
							detail: {
								originalTokenCount: metadata.originalTokenCount,
								summaryTokenCount: metadata.summaryTokenCount,
								reductionPercentage: metadata.reductionPercentage,
							},
						})
					);
				}

				console.log(
					`[ContextTracker] Context summary processed successfully. Reduced from ${metadata.originalTokenCount || 'unknown'} to ${metadata.summaryTokenCount || 'unknown'} tokens (${metadata.reductionPercentage ? metadata.reductionPercentage.toFixed(1) : 'unknown'}% reduction)`
				);
				
				// Update timestamp to prevent immediate re-triggering of summary
				this.lastSummaryTimestamp = Date.now();
			} catch (error) {
				console.error('[ContextTracker] Error processing summary notification:', error);
			} finally {
				this.isSummarizing = false;
				this.pendingSummaryJobId = null;
			}
		} else if (notification.type === 'context_summary_failed') {
			console.error('[ContextTracker] Context summary failed:', notification.error);
			this.isSummarizing = false;
			this.pendingSummaryJobId = null;

			// Handle specific error types
			const errorMessage = notification.error || 'Unknown error';
			if (errorMessage.includes('Premium') || errorMessage.includes('premium')) {
				console.warn(
					'[ContextTracker] Context summary denied: Premium subscription required'
				);
				// Don't retry on premium errors - user needs to upgrade
				this.lastSummaryTimestamp = Date.now();
			} else if (
				errorMessage.includes('unavailable') ||
				errorMessage.includes('timeout') ||
				errorMessage.includes('ECONNREFUSED') ||
				errorMessage.includes('ETIMEDOUT')
			) {
				console.warn(
					'[ContextTracker] Summarization service unavailable, will retry after interval'
				);
				// Allow retry after half interval for service errors
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			} else {
				// For other errors, allow retry after half interval
				console.warn(
					'[ContextTracker] Unexpected error during summarization, will retry after interval'
				);
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			}
		}
	}

	/**
	 * Check if user is logged in
	 * Returns true if user data exists in the store
	 */
	private async isUserLoggedIn(): Promise<boolean> {
		try {
			// Dynamically import store to avoid circular dependencies
			// Only import when needed (web platform only)
			if (Platform.OS !== 'web') {
				return false;
			}

			// Use dynamic import to avoid circular dependency issues
			const storeModule = await import('@reduxLocal/persistState');
			const store = storeModule.store;

			if (!store) {
				return false;
			}

			const state = store.getState();
			const user = state?.promiseData?.data?.userData;

			// User is logged in if user data exists and has an ID
			return !!(user && user.id);
		} catch (error) {
			console.warn('Failed to check user login status:', error);
			return false; // Default to not logged in on error
		}
	}

	/**
	 * Check if user has premium subscription
	 * Only premium users can use AI summary feature
	 */
	private async isPremiumUser(): Promise<boolean> {
		try {
			// First check if user is logged in
			const isLoggedIn = await this.isUserLoggedIn();
			if (!isLoggedIn) {
				return false;
			}

			// Dynamically import store to avoid circular dependencies
			// Only import when needed (web platform only)
			if (Platform.OS !== 'web') {
				return false;
			}

			// Use dynamic import to avoid circular dependency issues
			const storeModule = await import('@reduxLocal/persistState');
			const store = storeModule.store;

			if (!store) {
				return false;
			}

			const state = store.getState();
			const user = state?.promiseData?.data?.userData;

			if (!user) {
				return false;
			}

			// Check multiple premium indicators
			const isPremium = user?.isPremium === true;
			const hasActiveSubscription = user?.subscriptionStatus === 'active';
			const hasBillingActive =
				user?.billing?.isActive === true || user?.billing_info;

			// Check billing_info for active subscription
			let hasActiveBilling = false;
			if (user?.billing_info) {
				const billingInfo =
					typeof user.billing_info === 'object'
						? Object.values(user.billing_info).find(
								(billing: any) => billing?.isActive
							)
						: null;
				hasActiveBilling = Boolean(billingInfo);
			}

			// Check if subscription is not a free plan
			let isNotFreePlan = false;
			if (user?.billing_info) {
				const billingInfo =
					typeof user.billing_info === 'object'
						? Object.values(user.billing_info).find(
								(billing: any) => billing?.isActive
							)
						: user.billing_info;

				if (billingInfo) {
					const productName =
						billingInfo?.productName?.toLowerCase() || '';
					isNotFreePlan =
						!productName.includes('free') &&
						!productName.includes('starter') &&
						!productName.includes('exempt');
				}
			}

			return (
				isPremium ||
				hasActiveSubscription ||
				hasBillingActive ||
				(hasActiveBilling && isNotFreePlan)
			);
		} catch (error) {
			console.warn('Failed to check premium status:', error);
			return false; // Default to non-premium on error
		}
	}

	/**
	 * Schedule a debounced summary check
	 * NOTE: This method is no longer called automatically.
	 * Summary generation should only be triggered:
	 * 1. Manually by user via UI button (handleTriggerSummary)
	 * 2. As a one-time background job on the backend when context is full
	 * 
	 * This prevents continuous bombardment of context summary API calls.
	 * Automatic triggering has been removed to make summary generation
	 * a true background job that only happens once per user when needed.
	 */
	private scheduleSummaryCheck(): void {
		// Clear existing timeout
		if (this.summaryCheckTimeout) {
			clearTimeout(this.summaryCheckTimeout);
		}

		// Only check every 30 seconds to avoid too frequent checks
		this.summaryCheckTimeout = setTimeout(async () => {
			// Check if user is logged in before proceeding
			const isLoggedIn = await this.isUserLoggedIn();
			if (!isLoggedIn) {
				console.log('[ContextTracker] User not logged in, skipping summary check');
				return;
			}

			this.checkAndTriggerSummary().catch((err) => {
				console.error('Error in summary check:', err);
			});
		}, 30000); // 30 seconds debounce
	}

	/**
	 * Check if summary should be triggered and trigger it if conditions are met
	 */
	private async checkAndTriggerSummary(): Promise<void> {
		// First check if user is logged in - don't proceed if not authenticated
		const isLoggedIn = await this.isUserLoggedIn();
		if (!isLoggedIn) {
			console.log('[ContextTracker] User not logged in, skipping summary generation');
			return;
		}

		// Check if enough time has passed since last summary
		const timeSinceLastSummary = Date.now() - this.lastSummaryTimestamp;
		if (timeSinceLastSummary < MIN_SUMMARY_INTERVAL_MS) {
			console.log(
				`Summary requested too recently (${Math.round(timeSinceLastSummary / 1000)}s ago), skipping. Minimum interval: ${MIN_SUMMARY_INTERVAL_MS / 1000}s`
			);
			return;
		}

		// Check premium status - only premium users can use AI summaries
		const isPremium = await this.isPremiumUser();
		if (!isPremium) {
			console.log(
				'[ContextTracker] AI context summary is only available for premium accounts'
			);
			return;
		}

		// Check context size - only trigger when we're at or above the threshold (context window is full)
		const currentSize = await this.getContextSize(false); // Don't use cache for accurate check

		// Trigger when context window is full (at or above threshold)
		if (
			currentSize.tokens >= SUMMARY_TRIGGER_THRESHOLD_TOKENS ||
			currentSize.bytes >= SUMMARY_TRIGGER_THRESHOLD_BYTES
		) {
			console.log(
				`[ContextTracker] Context window is full (${currentSize.tokens} tokens >= ${SUMMARY_TRIGGER_THRESHOLD_TOKENS} threshold), triggering summary generation`
			);
			// Trigger summary generation
			await this.triggerSummaryGeneration();
		} else {
			console.log(
				`[ContextTracker] Context size below threshold (${currentSize.tokens} tokens < ${SUMMARY_TRIGGER_THRESHOLD_TOKENS} threshold), skipping summary generation`
			);
		}
	}

	private async triggerSummaryGeneration(): Promise<void> {
		// Prevent concurrent summary generation
		if (this.isSummarizing) {
			console.log('[ContextTracker] Summary generation already in progress, skipping...');
			return;
		}

		// Double-check user is logged in before making API call
		const isLoggedIn = await this.isUserLoggedIn();
		if (!isLoggedIn) {
			console.log(
				'[ContextTracker] User not logged in, cannot generate context summary'
			);
			return;
		}

		// Double-check premium status before making API call
		const isPremium = await this.isPremiumUser();
		if (!isPremium) {
			console.log(
				'[ContextTracker] AI context summary is only available for premium accounts'
			);
			return;
		}

		// Double-check context size before summarizing - must be at or above threshold
		const currentSize = await this.getContextSize(false);
		if (
			currentSize.tokens < SUMMARY_TRIGGER_THRESHOLD_TOKENS &&
			currentSize.bytes < SUMMARY_TRIGGER_THRESHOLD_BYTES
		) {
			console.log(
				'[ContextTracker] Context size below threshold, skipping summary generation'
			);
			return;
		}

		// Check minimum interval again
		const timeSinceLastSummary = Date.now() - this.lastSummaryTimestamp;
		if (timeSinceLastSummary < MIN_SUMMARY_INTERVAL_MS) {
			console.log('[ContextTracker] Summary requested too recently, skipping');
			return;
		}

		this.isSummarizing = true;
		this.lastSummaryTimestamp = Date.now(); // Update timestamp before making request

		// Set up socket listener if not already set up
		await this.setupSocketListener();

		try {
			// Import API function dynamically to avoid circular dependencies
			const apisModule = await import('@api/api');
			const apis = apisModule.default || apisModule;
			const entries = await this.getContextEntries();

			if (entries.length === 0) {
				this.isSummarizing = false;
				return;
			}

			console.log(
				`[ContextTracker] Queuing context summary job for ${entries.length} entries (${currentSize.tokens} tokens, ${currentSize.bytes} bytes)...`
			);

			// Call API to queue summary job (now returns job ID immediately)
			const response = await (apis as any).summarizeContext(entries);

			// Validate response structure - now expects job ID response
			if (!response || !response.success) {
				throw new Error(
					response?.error ||
						'Invalid response from summarization service'
				);
			}

			// Store job ID for tracking
			if (response.jobId) {
				this.pendingSummaryJobId = response.jobId;
				console.log(
					`[ContextTracker] Context summary job queued successfully. Job ID: ${response.jobId}. Waiting for completion notification...`
				);
			} else {
				// Fallback: if response still has summary (backward compatibility), process it
				if (response.summary && response.summary.content) {
					console.warn(
						'[ContextTracker] Received synchronous summary response (legacy mode). Processing directly...'
					);
					await this.processSummaryResponse(response.summary, entries.length);
					this.isSummarizing = false;
				} else {
					throw new Error('Summary response missing jobId or summary content');
				}
			}

			// Note: isSummarizing will be set to false when notification arrives
			// or in error handler below
		} catch (error: any) {
			console.error('[ContextTracker] Failed to queue context summary:', error);

			// Handle specific error types
			if (error?.requiresPremium || error?.response?.status === 403) {
				console.warn(
					'[ContextTracker] Context summary denied: Premium subscription required'
				);
				// Don't retry on 403 - user needs to upgrade
				this.lastSummaryTimestamp = Date.now();
			} else if (
				error?.retryable ||
				error?.response?.status === 503 ||
				error?.code === 'ECONNREFUSED' ||
				error?.code === 'ETIMEDOUT'
			) {
				console.warn(
					'[ContextTracker] Summarization service unavailable, will retry after interval'
				);
				// Allow retry after half interval for service errors
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			} else if (error?.response?.status === 400) {
				console.error(
					'[ContextTracker] Invalid request to summarization service:',
					error.response?.data
				);
				// Don't retry on 400 - it's a client error
				this.lastSummaryTimestamp = Date.now();
			} else {
				// For other errors, allow retry after half interval
				console.warn(
					'[ContextTracker] Unexpected error during summarization, will retry after interval'
				);
				this.lastSummaryTimestamp =
					Date.now() - MIN_SUMMARY_INTERVAL_MS * 0.5;
			}

			this.isSummarizing = false;
			this.pendingSummaryJobId = null;
		}
	}

	/**
	 * Process summary response (used for backward compatibility or direct processing)
	 * When context window is full: clears old context and saves summary for reference
	 */
	public async processSummaryResponse(
		summary: any,
		originalEntryCount: number
	): Promise<void> {
		// Validate summary metadata
		const metadata = summary.metadata || {};
		if (
			metadata.originalEntryCount === undefined ||
			metadata.originalTokenCount === undefined
		) {
			console.warn(
				'[ContextTracker] Summary response missing some metadata fields, proceeding anyway'
			);
		}

		// When context window is full: clear old context and save summary for reference
		// Step 1: Clear all old context entries to free up space
		await this.clearContext();

		// Step 2: Add the summary as a new context entry for future reference
		// The summary will be included in next AI calls, preserving important information
		await this.addContextEntry('ai_request', summary.content, {
			...metadata,
			isSummary: true,
			originalEntryCount: originalEntryCount,
			summarizedAt: new Date().toISOString(),
		});

		// Step 3: Wait for batch processing to complete and ensure the summary entry is saved
		// This prevents the summary from being lost and ensures context is properly reset
		while (this.isProcessing || this.contextQueue.length > 0) {
			await new Promise(resolve => setTimeout(resolve, 50));
		}
		
		// Wait an additional tick to ensure IndexedDB transaction completes
		await new Promise(resolve => setImmediate(resolve));

		// Invalidate cache to force immediate refresh
		this.invalidateSizeCache();

		// Dispatch custom event to notify UI components (like ContextProgressRing) to refresh
		if (Platform.OS === 'web' && typeof window !== 'undefined') {
			window.dispatchEvent(
				new CustomEvent('contextSummaryGenerated', {
					detail: {
						originalTokenCount: metadata.originalTokenCount,
						summaryTokenCount: metadata.summaryTokenCount,
						reductionPercentage: metadata.reductionPercentage,
					},
				})
			);
		}

		console.log(
			`[ContextTracker] Context summary processed successfully. Reduced from ${metadata.originalTokenCount || 'unknown'} to ${metadata.summaryTokenCount || 'unknown'} tokens (${metadata.reductionPercentage ? metadata.reductionPercentage.toFixed(1) : 'unknown'}% reduction)`
		);
		
		// Update timestamp to prevent immediate re-triggering of summary
		this.lastSummaryTimestamp = Date.now();
	}
}

// Create singleton instance
const contextTracker = new ContextTracker();

// Export functions
export const addContextEntry = (
	type: ContextEntryType,
	content: string,
	metadata?: Record<string, any>
) => contextTracker.addContextEntry(type, content, metadata);

export const getContextSize = () => contextTracker.getContextSize();

export const getContextEntries = (limit?: number) =>
	contextTracker.getContextEntries(limit);

export const getContextBreakdown = () => contextTracker.getContextBreakdown();

export const clearContext = () => contextTracker.clearContext();

export const updateContextSettings = (settings: Partial<ContextSettings>) =>
	contextTracker.updateSettings(settings);

export const getContextSettings = () => contextTracker.getSettings();

/**
 * Process a summary response - clears old context and saves summary for reference
 * This is used when a summary is generated and needs to replace the old context
 */
export const processSummaryResponse = async (
	summary: any,
	originalEntryCount: number
): Promise<void> => {
	return contextTracker.processSummaryResponse(summary, originalEntryCount);
};

/**
 * Set up socket listener for context summary notifications
 * This ensures the socket listener is ready to receive summary completion notifications
 */
export const setupSocketListener = async (): Promise<void> => {
	return contextTracker.setupSocketListener();
};

/**
 * Get all summary entries from context
 * Returns entries that have isSummary flag in metadata
 */
export const getSummaryEntries = async (): Promise<ContextEntry[]> => {
	if (Platform.OS !== 'web') return [];

	try {
		const entries = await contextTracker.getContextEntries();
		return entries.filter((entry) => entry.metadata?.isSummary === true);
	} catch (error) {
		console.error('Failed to get summary entries:', error);
		return [];
	}
};

export default contextTracker;
